import { Countries } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { FormsInputDropdown, InlineFormInput, TimezonePicker } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Section10.png';

type FormValues = {
  repCountry: number;
  repCity: string;
  repTimezone: string;
};

export const OrgRepLocationSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const countriesList = Countries.useGetCountries();

  const countryOptions =
    countriesList.isSuccess ?
      countriesList.data.map((country) => ({
        text: country.name,
        textValue: country.id,
      }))
    : [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      repCountry: getApplicationDataByKey('repCountry')?.toString() || '',
      repCity: getApplicationDataByKey('repCity'),
      repTimezone:
        getApplicationDataByKey('repTimezone') || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      repCountry: data.repCountry,
      repCity: data.repCity,
      repTimezone: data.repTimezone,
    });
    onContinue();
  };

  const updateTimezone = (timezone: string) => {
    setValue('repTimezone', timezone);
  };

  const handleBack = () => {
    updateApplicationData({
      repCountry: watch('repCountry'),
      repCity: watch('repCity'),
      repTimezone: watch('repTimezone'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title="Now, let's get to know you."
        description="These details will help us build your personal profile as your organization's representative."
      >
        <FormsInputDropdown
          control={control}
          name='repCountry'
          rules={{ required: 'Select your country to continue' }}
          label='Select your country'
          placeholder='Start typing country name'
          items={countryOptions}
          type='default'
          error={errors.repCountry}
        />
        <InlineFormInput
          label='City'
          placeholder='Enter the city where you live'
          register={() =>
            register('repCity', {
              required: 'City is required',
            })
          }
          errors={errors.repCity}
        />
        <TimezonePicker timezone={watch('repTimezone')} updateTimezone={updateTimezone} />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
